import { FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventDetailsTab } from '../../../../context';
import { Event, EventUserStatus } from '../../../../services';
import { useAppSelector, useEventDiscussionFeed, useIsNarrowViewport } from '../../../hooks';
import { optArrItem } from '../../../utils';
import { EventItemContent } from '../../EventItem';
import { TabItem, Tabs } from '../../Tabs';
import { EventDiscussionFeed } from '../EventDiscussionFeed';
import { EventParticipantsList } from '../EventParticipantsList';

import classNames from 'classnames';
import classes from './EventDetailsCommon.module.scss';

enum EventTab {
  DESCRIPTION,
  DISCUSSION,
  HOSTS,
  PARTICIPANTS,
  WAITLIST,
}

interface EventDetailsCommonProps {
  event: Event;
  onDeleteSuccess: () => void;
  initialTab?: EventDetailsTab;
  noPadding?: boolean;
  scrollableTarget?: string;
}

export const EventDetailsCommon: FunctionComponent<EventDetailsCommonProps> = memo(
  ({ event, onDeleteSuccess, initialTab, noPadding, scrollableTarget }) => {
    const { t: ctxT } = useTranslation('', { keyPrefix: 'eventDetails' });

    const { totalItems } = useAppSelector(({ eventDiscussionFeed }) => eventDiscussionFeed);

    const isNarrowViewport = useIsNarrowViewport();

    const {
      id: eventId,
      descriptionHTML = '',
      participantCountHost,
      participantCountConfirmed,
    } = event;

    const { allowToSeeParticipants = false, allowToSeeWaitlisted = false } =
      event.permissions ?? {};

    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(EventTab.DESCRIPTION);

    const { getEventDiscussionFeedItems } = useEventDiscussionFeed(eventId);

    useEffect(() => {
      getEventDiscussionFeedItems();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (initialTab === EventDetailsTab.PARTICIPANTS && allowToSeeParticipants) {
        setSelectedTabIndex(EventTab.PARTICIPANTS);
      }
    }, [allowToSeeParticipants, initialTab]);

    useEffect(() => {
      if (initialTab === EventDetailsTab.WAITLIST && allowToSeeWaitlisted) {
        setSelectedTabIndex(EventTab.WAITLIST);
      }
    }, [allowToSeeWaitlisted, initialTab]);

    const onSelectTab = useCallback(
      (tab: EventDetailsTab) => {
        if (tab === EventDetailsTab.PARTICIPANTS && allowToSeeParticipants) {
          setSelectedTabIndex(EventTab.PARTICIPANTS);
        } else if (tab === EventDetailsTab.WAITLIST && allowToSeeWaitlisted) {
          setSelectedTabIndex(EventTab.WAITLIST);
        }
      },
      [allowToSeeParticipants, allowToSeeWaitlisted]
    );

    const tabItems: TabItem[] = useMemo(
      () => [
        {
          index: EventTab.DESCRIPTION,
          name: ctxT('description'),
          content: (
            <div className={classes['event-details__description']}>
              {descriptionHTML ? (
                <div dangerouslySetInnerHTML={{ __html: descriptionHTML }} />
              ) : (
                <div className={classes['event-details__no-description']}>
                  {ctxT('no-description')}
                </div>
              )}
            </div>
          ),
        },
        {
          index: EventTab.DISCUSSION,
          name: `${ctxT('discussion')}${totalItems ? ` (${totalItems})` : ''}`,
          content: <EventDiscussionFeed eventId={eventId} scrollableTarget={scrollableTarget} />,
        },
        {
          index: EventTab.HOSTS,
          name: `${ctxT('hosts')}${participantCountHost ? ` (${participantCountHost})` : ''}`,
          content: (
            <EventParticipantsList
              eventId={eventId}
              status={EventUserStatus.HOST}
              scrollableTarget={scrollableTarget}
            />
          ),
        },
        ...optArrItem(allowToSeeParticipants, {
          index: EventTab.PARTICIPANTS,
          name: `${ctxT('participants')}${
            participantCountConfirmed ? ` (${participantCountConfirmed})` : ''
          }`,
          content: (
            <EventParticipantsList
              eventId={eventId}
              status={EventUserStatus.CONFIRMED}
              scrollableTarget={scrollableTarget}
            />
          ),
        }),
        ...optArrItem(allowToSeeWaitlisted, {
          index: EventTab.WAITLIST,
          name: ctxT('waitlist'),
          content: (
            <EventParticipantsList
              eventId={eventId}
              status={EventUserStatus.WAITLISTED}
              scrollableTarget={scrollableTarget}
            />
          ),
        }),
      ],
      [
        allowToSeeParticipants,
        allowToSeeWaitlisted,
        ctxT,
        descriptionHTML,
        totalItems,
        eventId,
        participantCountConfirmed,
        participantCountHost,
        scrollableTarget,
      ]
    );

    return (
      <div
        className={classNames(classes['event-details'], {
          [classes['event-details--no-padding']]: noPadding,
        })}
      >
        <EventItemContent
          event={event}
          noPadding={noPadding}
          onDeleteSuccess={onDeleteSuccess}
          onSelectTab={onSelectTab}
        />
        <div className={classes['event-details__tabs-wrapper']}>
          <Tabs
            items={tabItems}
            selectedTabIndex={selectedTabIndex}
            setSelectedTabIndex={setSelectedTabIndex}
            scrollable={isNarrowViewport}
            tabContentClassName={classes['event-details__tabs-content']}
            noBackground
          />
        </div>
      </div>
    );
  }
);
